import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { CapsuleTrend } from '@/components';
import { LstBenchmarkClass, LstBenchmarkPill } from '@/components/Charts/LstHistorical';
import { NCCardSectionHeader, printLatestDate } from '@/components/NCCard/NCCard';
import { getNCChangeIcon, NCFactCapsule } from '@/components/NCFactCapsule/NCFactCapsule';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { isNullish } from '@/utils/booleans';

export type LstStatsTileProps = {
  latestFact: PlotReportFactElement<number | null> | undefined;
  oneYearUpliftFact: PlotReportFactElement<number | null> | undefined;
  oneYearPctChangeFact: PlotReportFactElement<number | null> | undefined;
  benchmark: LstBenchmarkClass | undefined;
};

const isWarming = (fact: PlotReportFactElement<number | null> | undefined) =>
  typeof fact?.value === 'number' && fact.value > 0;

export const LstStatsTile = ({ latestFact, oneYearUpliftFact, oneYearPctChangeFact, benchmark }: LstStatsTileProps) => {
  const { t } = useTranslation();

  const lastMeasuredDate = printLatestDate([latestFact?.measured_at]);
  const upliftYear = oneYearUpliftFact?.measured_at ? new Date(oneYearUpliftFact.measured_at).getFullYear() : null;

  return (
    <Stack className='justify-center gap-8 p-6 lg:gap-4 lg:p-8 lg:pr-2' data-testid='lst-stats-tile'>
      <NCCardSectionHeader
        title=''
        supplementaryInfo={`${t('shared.ncaDetail.details.labels.latestAnalysis')} ${lastMeasuredDate}`}
      />
      <Stack direction='row' className='flex-wrap gap-6'>
        <NCFactCapsule
          label={t('shared.ncaDetail.details.labels.latestAnalysis')}
          fact={latestFact}
          trend={
            typeof oneYearPctChangeFact?.value === 'number' && (
              <CapsuleTrend variant={isWarming(oneYearPctChangeFact) ? 'negative' : 'positive'}>
                {`${getDisplayNumber(oneYearPctChangeFact.value, undefined, { signDisplay: 'always' })} %`}
              </CapsuleTrend>
            )
          }
          capsuleIconProps={{ icon: getNCChangeIcon(latestFact), variant: 'default' }}
          infoPopoverProps={{
            title: t('shared.ncaDetail.details.explainers.latestAnalysis.title'),
            body: t('shared.ncaDetail.details.explainers.latestAnalysis.body', { date: lastMeasuredDate }),
          }}
        />
        {!isNullish(oneYearUpliftFact?.value) && (
          <NCFactCapsule
            label={t('shared.ncaDetail.details.labels.oneYearChange')}
            fact={oneYearUpliftFact}
            capsuleIconProps={{
              icon: getNCChangeIcon(oneYearUpliftFact),
              variant: isWarming(oneYearUpliftFact) ? 'negative' : 'default',
            }}
            infoPopoverProps={{
              title: t('shared.ncaDetail.details.landSurfaceTemperature.explainers.oneYearChange.title'),
              body: t('shared.ncaDetail.details.landSurfaceTemperature.explainers.oneYearChange.body', {
                year: upliftYear,
                previousYear: upliftYear ? upliftYear - 1 : null,
              }),
              popoverTriggerProps: { 'data-testid': 'lst-one-year-change-tooltip' },
            }}
          />
        )}
        {!!benchmark && (
          <Stack className='mt-4 gap-4 rounded-2xl bg-neutral-black-4 px-4 py-6' data-testid='lst-benchmark'>
            <LstBenchmarkPill benchmarkType={benchmark} />
            <span className='typography-body1Bold'>
              {t('shared.ncaDetail.details.landSurfaceTemperature.labels.whatDoesThisMean')}
            </span>
            <p className='typography-body1 text-text-secondary'>
              {t(`shared.ncaDetail.details.landSurfaceTemperature.explainers.benchmark.${benchmark}`)}
            </p>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
